import { isMobileWeb } from '@toss/utils';
import html2canvas from 'html2canvas';
import { useTranslation } from 'next-i18next';
import './events.quiz-share';
import { trackEvent } from '@zep/logger/tracking';
import { useViewport } from '@/hooks';
import { cn } from '@/utils/tailwind.util';
import { DesktopQRCodeSection } from './DesktopQRCodeSection';
import { EnterCodeSection } from './EnterCodeSection';
import type { ShareContentType, ShareMethod } from './events.quiz-share';
import { LinkCopySection } from './LinkCopySection';
import { MobileQRCodeSection } from './MobileQRCodeSection';
interface ShareContentProps {
  title: string;
  enterCode: string | number;
  shareUrl: string;
  /** 공유 수단 이벤트의 출처(모달을 연 화면). */
  place: string;
  contentType: ShareContentType;
  /** 공유 대상 식별자. 이벤트마다 TraceId 가 달라 다른 이벤트로 잇는 건 안전하지 않다. */
  contentId: string;
  /** 학급 맥락에서 열렸다면 그 학급. 여러 학급을 운영하는 교사를 가른다. */
  classroomId?: string;
  /** 내 콘텐츠인지. 판정할 수 없으면(비로그인 등) undefined — 추측해 싣지 않는다. */
  isOwner?: boolean;
  obscured?: boolean;
}
export function ShareContent({
  enterCode,
  shareUrl,
  title,
  place,
  contentType,
  contentId,
  classroomId,
  isOwner,
  obscured
}: ShareContentProps) {
  const {
    t
  } = useTranslation(['common']);
  const {
    isMobile
  } = useViewport();
  const isMobileOrTablet = isMobileWeb() || isMobile;

  // 공유 수단은 「공유 → 학생 접속」 퍼널의 첫 칸이다. QR 은 캔버스 변환이 실패할 수
  // 있어 클릭이 아니라 결과가 확정된 뒤에 발화한다.
  const trackShareMethod = (share_method: ShareMethod, result: 'success' | 'fail') => {
    trackEvent('quiz_share_method_click', {
      share_method,
      result,
      place,
      content_type: contentType,
      content_id: contentId,
      ...(classroomId && {
        classroom_id: classroomId
      }),
      ...(isOwner !== undefined && {
        is_owner: isOwner
      })
    });
  };
  const handleCanvasError = (message: string, error: unknown) => {
    console.error(message, error);
    alert(`${t('googleClassroom.unknownErrorDialog.title')}\n${t('googleClassroom.unknownErrorDialog.content')}`);
  };
  const createQRCodeCanvas = (qrCode: HTMLDivElement) => html2canvas(qrCode, {
    backgroundColor: 'white',
    scale: 2
  });
  const handleCopyQRImage = async (qrCode: HTMLDivElement, onSuccess?: () => void) => {
    try {
      const clipboardItem = new ClipboardItem({
        'image/png': (async () => {
          const canvas = await createQRCodeCanvas(qrCode);
          return new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            }, 'image/png');
          });
        })()
      });
      await navigator.clipboard.write([clipboardItem]);
      trackShareMethod('qr_copy', 'success');
      onSuccess?.();
    } catch (error) {
      trackShareMethod('qr_copy', 'fail');
      handleCanvasError('Failed to copy QR code image', error);
    }
  };
  const handleDownloadQRCode = async (qrCode: HTMLDivElement) => {
    try {
      const canvas = await createQRCodeCanvas(qrCode);
      const link = document.createElement('a');
      link.download = `qr-code-${title}.png`;
      link.href = canvas.toDataURL();
      link.click();
      trackShareMethod('qr_download', 'success');
    } catch (error) {
      trackShareMethod('qr_download', 'fail');
      handleCanvasError('Failed to download QR code', error);
    }
  };

  // pointer-events-none 은 포인터만 막고 탭 이동은 막지 못한다. 그것만 두면 흐림 뒤의
  // 입장 링크·QR 복사/다운로드·링크 복사 버튼에 포커스가 남아 보이지 않는 것을 실행하게
  // 되고, aria-hidden 안에 포커스 가능한 요소가 남아 접근성 트리도 깨진다. inert 가
  // 포인터·포커스·보조기술 노출을 한 번에 끊는다.
  //
  // React 18.3.1 은 boolean inert 를 렌더하지 않아 빈 문자열 핵을 쓴다
  // (JellyGrantDialog·BuildPageView 와 같은 방식).
  // TODO(React 19): inert={obscured} 로 정리.
  const obscuredInertProps = (obscured ? {
    inert: ''
  } : {}) as unknown as React.HTMLAttributes<HTMLDivElement>;
  return <div {...obscuredInertProps} aria-hidden={obscured || undefined} className={cn('flex min-h-0 w-full flex-1 flex-col gap-md mobile-lg:flex-none', obscured && 'pointer-events-none select-none')} data-sentry-component="ShareContent" data-sentry-source-file="ShareContent.tsx">
      <div className={cn('flex min-h-0 w-full flex-1 flex-col items-center gap-xs rounded-md bg-neutral px-md py-lg max-mobile-lg:overflow-hidden mobile-lg:flex-none', obscured && 'blur-[2.5px]')}>
        <EnterCodeSection enterCode={enterCode} obscured={obscured} onCodeCopied={succeeded => trackShareMethod('enter_code_copy', succeeded ? 'success' : 'fail')} data-sentry-element="EnterCodeSection" data-sentry-source-file="ShareContent.tsx" />
        {isMobileOrTablet ? <MobileQRCodeSection url={shareUrl} onDownload={handleDownloadQRCode} onCopy={handleCopyQRImage} /> : <DesktopQRCodeSection url={shareUrl} onDownload={handleDownloadQRCode} onCopy={handleCopyQRImage} />}
      </div>
      <LinkCopySection url={shareUrl} obscured={obscured} onCopied={succeeded => trackShareMethod('link_copy', succeeded ? 'success' : 'fail')} data-sentry-element="LinkCopySection" data-sentry-source-file="ShareContent.tsx" />
    </div>;
}